import React, { useState, useEffect } from "react";
import { Row, Col, Button, Spin, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { FilterOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import CandidateTestCard from "../components/CandidateTestCard";
import TestFilter from "../components/TestFilter";
import theme from "../theme";
import api from "../services/api";
import { toast } from "react-toastify";
import { useCart } from "../contexts/CartContext";

interface Test {
  id: number;
  name: string;
  price: number;
  industry_name: string;
  industry_color: string;
  industry_id: number;
  software_name: string;
  software_id: number;
  question_count: number;
  time_limit: number;
  passing_grade: number;
  description?: string;
}

interface FilterValues {
  industries: number[];
  software: number[];
  questionRange: [number, number];
  timeLimit: {
    timed: boolean;
    timeRange: [number, number];
    noTimeLimit: boolean;
  };
}


const BrowseTests: React.FC = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState<Test[]>([]);
  const [allTests, setAllTests] = useState<Test[]>([]);
  const [filteredTests, setFilteredTests] = useState<Test[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [displayCount, setDisplayCount] = useState(12);
  const [maxQuestions, setMaxQuestions] = useState(50);
  const [maxTimeLimit, setMaxTimeLimit] = useState(90);
  const [searchTerm, setSearchTerm] = useState("");
  const { isInCart } = useCart();

  useEffect(() => {
    loadTests();
  }, []);

  useEffect(() => {
    setTests(filteredTests.slice(0, displayCount));
  }, [filteredTests, displayCount]);

  const loadTests = async () => {
    try {
      setLoading(true);
      const response = await api.get('browse-tests/');
      const testsWithDescription = response.data.map((test: any) => ({
        ...test,
        description: test.description || "No description available.",
      }));

      // Calculate max values for filters
      const maxQ = Math.max(...testsWithDescription.map((test: Test) => test.question_count));
      const maxT = Math.max(...testsWithDescription.map((test: Test) => test.time_limit).filter((t: number) => t > 0));

      setMaxQuestions(maxQ);
      setMaxTimeLimit(maxT);
      setAllTests(testsWithDescription);
      setFilteredTests(testsWithDescription);
      setTests(testsWithDescription.slice(0, displayCount));
    } catch (error: any) {
      console.error('Failed to load assessments:', error);
      toast.error('Failed to load assessments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTest = (testId: number) => {
    navigate(`/test-details?test_id=${testId}`);
  };

  const applyFilters = (filters?: FilterValues, search?: string) => {
    let filtered = [...allTests];
    const currentSearch = search !== undefined ? search : searchTerm;

    // Filter by search term (test name)
    if (currentSearch.trim()) {
      filtered = filtered.filter(test =>
        test.name.toLowerCase().includes(currentSearch.toLowerCase())
      );
    }

    // Filter by industries
    if (filters?.industries && filters.industries.length > 0) {
      filtered = filtered.filter(test =>
        filters.industries.includes(test.industry_id)
      );
    }

    // Filter by software
    if (filters?.software && filters.software.length > 0) {
      filtered = filtered.filter(test =>
        test.software_id && filters.software.includes(test.software_id)
      );
    }

    // Filter by question count range
    if (filters?.questionRange) {
      filtered = filtered.filter(test =>
        test.question_count >= filters.questionRange[0] &&
        test.question_count <= filters.questionRange[1]
      );
    }

    // Filter by time limit
    if (filters?.timeLimit) {
      if (filters.timeLimit.timed && !filters.timeLimit.noTimeLimit) {
        // Only timed tests
        filtered = filtered.filter(test =>
          test.time_limit > 0 &&
          test.time_limit >= filters.timeLimit.timeRange[0] &&
          test.time_limit <= filters.timeLimit.timeRange[1]
        );
      } else if (!filters.timeLimit.timed && filters.timeLimit.noTimeLimit) {
        // Only no time limit tests
        filtered = filtered.filter(test => test.time_limit === 0);
      } else if (filters.timeLimit.timed && filters.timeLimit.noTimeLimit) {
        // Both timed and no time limit
        filtered = filtered.filter(test =>
          test.time_limit === 0 ||
          (test.time_limit >= filters.timeLimit.timeRange[0] &&
            test.time_limit <= filters.timeLimit.timeRange[1])
        );
      }
    }

    setFilteredTests(filtered);
    setDisplayCount(12); // Reset display count when filters change
  };

  const handleFilterChange = (filters: FilterValues) => {
    applyFilters(filters);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    applyFilters(undefined, value);
  };

  const loadMoreTests = () => {
    if (loadingMore || displayCount >= filteredTests.length) return;

    setLoadingMore(true);
    setTimeout(() => {
      setDisplayCount(prev => prev + 12);
      setLoadingMore(false);
    }, 500);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      if (scrollTop + windowHeight >= documentHeight - 1000) {
        loadMoreTests();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, displayCount, filteredTests.length]);

  return (
    <div style={bg}>
      {/* header */}
      {/* <Row justify="space-between" align="middle" style={{ marginBottom: 32 }}>
        <Row align="middle" gutter={12}>
          <Col>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/test-dashboard')}
              style={{ marginBottom: 16, color: '#666' }}
            >
              Back to Dashboard
            </Button>
            <h1 style={heading}>Browse assessments</h1>
          </Col>
        </Row>
      </Row> */}

      {/* Main content with filter sidebar */}
      <Row gutter={[32, 0]}>
        {/* Filter Sidebar */}
        <Col xs={24} lg={6}>
          <TestFilter
            onFilterApply={handleFilterChange}
            maxQuestions={maxQuestions}
            maxTimeLimit={maxTimeLimit}
          />
        </Col>

        {/* Tests Grid */}
        <Col xs={24} lg={18}>
          {/* Search Input */}
          <div className="input-form" style={searchContainer}>
            <Input
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              prefix={<SearchOutlined style={{ color: '#bbb' }} />}
              style={searchInput}
              size="large"
            />
          </div>

          <Row gutter={[32, 32]}>
            {loading ? (
              <Col span={24}>
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  Loading assessments...
                </div>
              </Col>
            ) : tests.length === 0 ? (
              <Col span={24}>
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  No assessments available.
                </div>
              </Col>
            ) : (
              tests.map(test => (
                <Col key={test.id} xs={24} sm={12} xl={8}>
                  <CandidateTestCard
                    id={test.id}
                    title={test.name}
                    price={test.price}
                    questionCount={test.question_count}
                    timeLimit={test.time_limit > 0 ? `${test.time_limit}` : "No time limit"}
                    description={test.description || "No description available."}
                    industryTags={[test.industry_name]}
                    industryColor={test.industry_color}
                    showCart={true}
                    inCart={isInCart(test.id)}
                    onClick={() => handleViewTest(test.id)}
                  />
                </Col>
              ))
            )}
          </Row>
        </Col>
      </Row>

      {/* Loading indicator for infinite scroll */}
      {loadingMore && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Spin size="large" />
          <div style={{ marginTop: 8, color: '#666' }}>Loading more assessments...</div>
        </div>
      )}

      {/* End of results indicator */}
      {!loading && displayCount >= filteredTests.length && filteredTests.length > 0 && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          You've reached the end of all assessments ({filteredTests.length} total)
        </div>
      )}
    </div>
  );
};


const bg: React.CSSProperties = {
  minHeight: "100vh",
};

const heading: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  color: theme.colors.primary,
  margin: 0,
};

const searchContainer: React.CSSProperties = {
  marginBottom: 24,
};

const searchInput: React.CSSProperties = {
	borderRadius: 24,
	width: "100%",
	background: "#eaf2ff",
	border: "none",
	color: "#23244A",
	fontWeight: 500,
	height: 32,
	fontSize: 14,
	paddingLeft: 24,
	paddingRight: 24,
};

export default BrowseTests;
